/**
 * measure.ts — geodesic distance & area calculations (no external dep)
 * Uses Haversine for distance and the Spherical Excess formula for area.
 */

export type Coord = [number, number]; // [lng, lat] degrees

const R = 6371008.8; // Earth mean radius metres

function toRad(d: number) {
  return (d * Math.PI) / 180;
}

/** Haversine distance between two WGS-84 points (metres) */
export function haversine(a: Coord, b: Coord): number {
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const sin2 = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(sin2));
}

/** Total polyline distance in metres */
export function lineDistance(coords: Coord[]): number {
  let d = 0;
  for (let i = 1; i < coords.length; i++) d += haversine(coords[i - 1], coords[i]);
  return d;
}

/** Spherical excess area of a polygon (metres²) – ring must be [lng,lat] */
export function polygonArea(ring: Coord[]): number {
  // Close the ring if needed
  const pts = [...ring];
  if (pts[0][0] !== pts[pts.length - 1][0] || pts[0][1] !== pts[pts.length - 1][1]) pts.push(pts[0]);
  if (pts.length < 4) return 0;

  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const [lng1, lat1] = pts[i].map(toRad) as [number, number];
    const [lng2, lat2] = pts[i + 1].map(toRad) as [number, number];
    total += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  return Math.abs((total * R * R) / 2);
}

/** Format metres → human-readable string */
export function formatDistance(m: number): string {
  if (m < 1000) return `${m.toFixed(1)} m`;
  return `${(m / 1000).toFixed(3)} km`;
}

/** Format m² → human-readable string */
export function formatArea(m2: number): string {
  if (m2 < 10_000) return `${m2.toFixed(1)} m²`;
  if (m2 < 1_000_000) return `${(m2 / 10_000).toFixed(3)} ha`;
  return `${(m2 / 1_000_000).toFixed(3)} km²`;
}
